import { FieldKind, type DocField, type FieldAnchor } from '../../_lib/docx-core';

export const FIELD_CLASS = 'docx-field';
export const FIELD_ID_ATTR = 'data-field-id';

const BLOCK_TAGS = new Set(['P', 'DIV', 'TABLE', 'UL', 'OL', 'SECTION']);

export interface OverlayHandlers {
  commit(id: string, text: string): void;
  toggle(id: string): void;
  choose(id: string, host: HTMLElement): void;
  focus(id: string): void;
  type(id: string, text: string): void;
}

export function bindFields(
  anchors: readonly FieldAnchor[],
  fields: readonly DocField[],
  handlers: OverlayHandlers,
): void {
  for (const anchor of anchors) {
    const field = fields.find((f) => f.id === anchor.id);
    if (!field) {
      continue;
    }

    bindOne(anchor, field, handlers);
  }
}

export function showValue(root: HTMLElement, id: string, text: string): void {
  for (const host of root.querySelectorAll<HTMLElement>(`.${FIELD_CLASS}`)) {
    if (host.getAttribute(FIELD_ID_ATTR) === id) {
      host.innerText = text;
    }
  }
}

function bindOne(anchor: FieldAnchor, field: DocField, handlers: OverlayHandlers): void {
  const host = wrapRange(anchor.start, anchor.end);
  if (!host) {
    return;
  }

  host.classList.add(FIELD_CLASS);
  host.setAttribute(FIELD_ID_ATTR, field.id);
  host.setAttribute('title', field.label);
  host.dataset['kind'] = field.kind;
  host.addEventListener('focus', () => handlers.focus(field.id));

  if (field.kind === FieldKind.Text || field.kind === FieldKind.RichText) {
    makeEditable(host, field, handlers);

    return;
  }

  host.tabIndex = 0;
  host.addEventListener('click', () => {
    if (field.kind === FieldKind.CheckBox) {
      handlers.toggle(field.id);

      return;
    }

    handlers.choose(field.id, host);
  });
}

function makeEditable(host: HTMLElement, field: DocField, handlers: OverlayHandlers): void {
  host.contentEditable = field.multiline ? 'true' : 'plaintext-only';

  let before = host.innerText;
  host.addEventListener('focus', () => (before = host.innerText));
  host.addEventListener('input', () => handlers.type(field.id, host.innerText));
  host.addEventListener('blur', () => {
    if (host.innerText !== before) {
      handlers.commit(field.id, host.innerText);
    }
  });

  if (field.placeholder) {
    host.addEventListener('focus', () => selectAll(host), { once: true });
  }

  if (field.multiline) {
    return;
  }

  host.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    host.blur();
  });
}

function selectAll(host: HTMLElement): void {
  const selection = host.ownerDocument.defaultView?.getSelection();
  if (!selection) {
    return;
  }

  const range = host.ownerDocument.createRange();
  range.selectNodeContents(host);

  selection.removeAllRanges();
  selection.addRange(range);
}

function wrapRange(start: HTMLElement, end: HTMLElement): HTMLElement | undefined {
  const parent = sharedParent(start, end);
  if (!parent) {
    return undefined;
  }

  const from = topChild(parent, start);
  const to = topChild(parent, end);
  if (!from || !to) {
    return undefined;
  }

  const nodes = siblingsBetween(from, to);
  const host = parent.ownerDocument.createElement(isBlockRange(nodes) ? 'div' : 'span');

  parent.insertBefore(host, from);
  host.append(...nodes);

  start.remove();
  end.remove();

  return host;
}

function sharedParent(a: HTMLElement, b: HTMLElement): HTMLElement | undefined {
  for (let node: HTMLElement | null = a.parentElement; node; node = node.parentElement) {
    if (node.contains(b)) {
      return node;
    }
  }

  return undefined;
}

function topChild(parent: HTMLElement, node: HTMLElement): Node | undefined {
  return Array.from(parent.childNodes).find((child) => child === node || child.contains(node));
}

function siblingsBetween(from: Node, to: Node): Node[] {
  const nodes: Node[] = [];

  for (let node: Node | null = from; node; node = node.nextSibling) {
    nodes.push(node);

    if (node === to) {
      break;
    }
  }

  return nodes;
}

function isBlockRange(nodes: readonly Node[]): boolean {
  return nodes.some((node) => BLOCK_TAGS.has(node.nodeName));
}
